import { lazy, Suspense, useEffect } from 'react';
import { Route, Routes, Link, useLocation } from 'react-router-dom';
import { useAuthBootstrap } from '@/auth';
import { Spinner } from '@/ui/primitives/Spinner';
import { Toaster } from '@/ui/overlays/Toast';

const ComponentShowcase = lazy(() =>
  import('@/ui/_dev/ComponentShowcase').then((m) => ({ default: m.ComponentShowcase })),
);

// Onboarding / auth
const SignInPage = lazy(() =>
  import('@/features/onboarding').then((m) => ({ default: m.SignInPage })),
);
const MagicLinkSentPage = lazy(() =>
  import('@/features/onboarding').then((m) => ({ default: m.MagicLinkSentPage })),
);
const AuthCallbackPage = lazy(() =>
  import('@/features/onboarding').then((m) => ({ default: m.AuthCallbackPage })),
);
const OnboardingPage = lazy(() =>
  import('@/features/onboarding').then((m) => ({ default: m.OnboardingPage })),
);
const ForgotPasswordPage = lazy(() =>
  import('@/features/onboarding').then((m) => ({ default: m.ForgotPasswordPage })),
);
const ResetPasswordPage = lazy(() =>
  import('@/features/onboarding').then((m) => ({ default: m.ResetPasswordPage })),
);

// Eligibility
const EligibilityWizardPage = lazy(() =>
  import('@/features/eligibility').then((m) => ({ default: m.EligibilityWizardPage })),
);
const EligibilityRejectionPage = lazy(() =>
  import('@/features/eligibility').then((m) => ({ default: m.EligibilityRejectionPage })),
);

// Portal shell
const PortalLayout = lazy(() =>
  import('@/features/portal-shell').then((m) => ({ default: m.PortalLayout })),
);

// Dashboard
const DashboardPage = lazy(() =>
  import('@/features/dashboard').then((m) => ({ default: m.DashboardPage })),
);

// Deals
const DealsListPage = lazy(() =>
  import('@/features/deals-list').then((m) => ({ default: m.DealsListPage })),
);
const DealBuilderPage = lazy(() =>
  import('@/features/deal-builder').then((m) => ({ default: m.DealBuilderPage })),
);
const ClaimedDealLandingPage = lazy(() =>
  import('@/features/deal-builder').then((m) => ({ default: m.ClaimedDealLandingPage })),
);

// Documents
const DocumentsPage = lazy(() =>
  import('@/features/documents').then((m) => ({ default: m.DocumentsPage })),
);
const DocumentsViewPage = lazy(() =>
  import('@/features/documents-view').then((m) => ({ default: m.DocumentsViewPage })),
);

// Lender comparison
const LenderComparisonPage = lazy(() =>
  import('@/features/lender-comparison').then((m) => ({ default: m.LenderComparisonPage })),
);

// Submission
const SubmissionConfirmPage = lazy(() =>
  import('@/features/submission').then((m) => ({ default: m.SubmissionConfirmPage })),
);
const SubmissionSubmittedPage = lazy(() =>
  import('@/features/submission').then((m) => ({ default: m.SubmissionSubmittedPage })),
);

// Case tracker
const CaseTrackerPage = lazy(() =>
  import('@/features/case-tracker').then((m) => ({ default: m.CaseTrackerPage })),
);

// Earnings
const EarningsPage = lazy(() =>
  import('@/features/earnings').then((m) => ({ default: m.EarningsPage })),
);

// Messages
const MessagesPage = lazy(() =>
  import('@/features/messages').then((m) => ({ default: m.MessagesPage })),
);

// Settings
const SettingsPage = lazy(() =>
  import('@/features/settings').then((m) => ({ default: m.SettingsPage })),
);

// CRM shell
const CRMLayout = lazy(() =>
  import('@/layouts/CRMLayout').then((m) => ({ default: m.CRMLayout })),
);

// CRM dashboard
const CRMDashboardPage = lazy(() =>
  import('@/features/crm-dashboard').then((m) => ({ default: m.CRMDashboardPage })),
);

// CRM borrowers
const BorrowersListPage = lazy(() =>
  import('@/features/crm-borrowers').then((m) => ({ default: m.BorrowersListPage })),
);
const BorrowerDetailPage = lazy(() =>
  import('@/features/crm-borrowers').then((m) => ({ default: m.BorrowerDetailPage })),
);

// CRM deals
const CRMDealsListPage = lazy(() =>
  import('@/features/crm-deals').then((m) => ({ default: m.CRMDealsListPage })),
);
const CRMDealDetailPage = lazy(() =>
  import('@/features/crm-deals').then((m) => ({ default: m.CRMDealDetailPage })),
);

// CRM document review
const DocumentReviewQueuePage = lazy(() =>
  import('@/features/crm-document-review').then((m) => ({ default: m.DocumentReviewQueuePage })),
);

// CRM audit log
const AuditLogPage = lazy(() =>
  import('@/features/crm-audit').then((m) => ({ default: m.AuditLogPage })),
);

// CRM submissions
const SubmissionsQueuePage = lazy(() =>
  import('@/features/crm-submissions').then((m) => ({ default: m.SubmissionsQueuePage })),
);
const SubmissionDetailPage = lazy(() =>
  import('@/features/crm-submissions').then((m) => ({ default: m.SubmissionDetailPage })),
);

// CRM lenders
const LendersListPage = lazy(() =>
  import('@/features/crm-lenders').then((m) => ({ default: m.LendersListPage })),
);
const LenderDetailPage = lazy(() =>
  import('@/features/crm-lenders').then((m) => ({ default: m.LenderDetailPage })),
);
const LenderCreatePage = lazy(() =>
  import('@/features/crm-lenders').then((m) => ({ default: m.LenderCreatePage })),
);

// CRM lender products
const LenderProductEditPage = lazy(() =>
  import('@/features/crm-lender-products').then((m) => ({ default: m.LenderProductEditPage })),
);

// CRM form templates
const FormTemplateUploadPage = lazy(() =>
  import('@/features/crm-form-templates').then((m) => ({ default: m.FormTemplateUploadPage })),
);
const FormTemplateMapperPage = lazy(() =>
  import('@/features/crm-form-templates').then((m) => ({ default: m.FormTemplateMapperPage })),
);

// CRM commission
const CommissionLedgerPage = lazy(() =>
  import('@/features/commission').then((m) => ({ default: m.CommissionLedgerPage })),
);
const CommissionDetailPage = lazy(() =>
  import('@/features/commission').then((m) => ({ default: m.CommissionDetailPage })),
);

// CRM payouts
const PayoutBatchListPage = lazy(() =>
  import('@/features/payouts').then((m) => ({ default: m.PayoutBatchListPage })),
);
const PayoutBatchDetailPage = lazy(() =>
  import('@/features/payouts').then((m) => ({ default: m.PayoutBatchDetailPage })),
);

// CRM analytics
const FunnelDashboardPage = lazy(() =>
  import('@/features/analytics').then((m) => ({ default: m.FunnelDashboardPage })),
);
const LenderAnalyticsDashboardPage = lazy(() =>
  import('@/features/analytics').then((m) => ({ default: m.LenderAnalyticsDashboardPage })),
);
const AICostDashboardPage = lazy(() =>
  import('@/features/analytics').then((m) => ({ default: m.AICostDashboardPage })),
);
const RevenueDashboardPage = lazy(() =>
  import('@/features/analytics').then((m) => ({ default: m.RevenueDashboardPage })),
);

// CRM BDMs
const BDMListPage = lazy(() =>
  import('@/features/crm-lenders').then((m) => ({ default: m.BDMListPage })),
);

// CRM agencies
const AgenciesPage = lazy(() =>
  import('@/features/crm-agencies').then((m) => ({ default: m.AgenciesPage })),
);

// CRM admin
const StaffUsersPage = lazy(() =>
  import('@/features/admin').then((m) => ({ default: m.StaffUsersPage })),
);
const SystemSettingsPage = lazy(() =>
  import('@/features/admin').then((m) => ({ default: m.SystemSettingsPage })),
);
const BrickflowSettingsPage = lazy(() =>
  import('@/features/admin').then((m) => ({ default: m.BrickflowSettingsPage })),
);

// CRM Brickflow finance
const LenderSearchPage = lazy(() =>
  import('@/features/crm-brickflow').then((m) => ({ default: m.LenderSearchPage })),
);
const BorrowerPortalPage = lazy(() =>
  import('@/features/crm-brickflow').then((m) => ({ default: m.BorrowerPortalPage })),
);
const DealPipelinePage = lazy(() =>
  import('@/features/crm-brickflow').then((m) => ({ default: m.DealPipelinePage })),
);
const DipTrackerPage = lazy(() =>
  import('@/features/crm-brickflow').then((m) => ({ default: m.DipTrackerPage })),
);
const BrickflowActionQueuePage = lazy(() =>
  import('@/features/crm-brickflow').then((m) => ({ default: m.BrickflowActionQueuePage })),
);
const ShortlistReviewQueuePage = lazy(() =>
  import('@/features/crm-brickflow').then((m) => ({ default: m.ShortlistReviewQueuePage })),
);
const ShortlistReviewDetailPage = lazy(() =>
  import('@/features/crm-brickflow').then((m) => ({ default: m.ShortlistReviewDetailPage })),
);
const BrickflowAnalyticsPage = lazy(() =>
  import('@/features/crm-brickflow').then((m) => ({ default: m.BrickflowAnalyticsPage })),
);

// CRM messaging
const CRMMessagingPage = lazy(() =>
  import('@/features/crm-messaging').then((m) => ({ default: m.CRMMessagingPage })),
);
const NotificationLogPage = lazy(() =>
  import('@/features/crm-messaging').then((m) => ({ default: m.NotificationLogPage })),
);
const TemplatesPage = lazy(() =>
  import('@/features/crm-messaging').then((m) => ({ default: m.TemplatesPage })),
);

// Marketing layout
const MarketingLayout = lazy(() =>
  import('@/layouts/MarketingLayout').then((m) => ({ default: m.MarketingLayout })),
);

// Marketing pages

const HomePage = lazy(() =>
  import('@/marketing').then((m) => ({ default: m.HomePage })),
);
const HowItWorksPage = lazy(() =>
  import('@/marketing').then((m) => ({ default: m.HowItWorksPage })),
);
const YourSharePage = lazy(() =>
  import('@/marketing').then((m) => ({ default: m.YourSharePage })),
);
const BridgingFinancePage = lazy(() =>
  import('@/marketing').then((m) => ({ default: m.BridgingFinancePage })),
);
const CommercialMortgagesPage = lazy(() =>
  import('@/marketing').then((m) => ({ default: m.CommercialMortgagesPage })),
);
const DevelopmentFinancePage = lazy(() =>
  import('@/marketing').then((m) => ({ default: m.DevelopmentFinancePage })),
);
const BuyToLetPage = lazy(() =>
  import('@/marketing').then((m) => ({ default: m.BuyToLetPage })),
);
const SemiCommercialPage = lazy(() =>
  import('@/marketing').then((m) => ({ default: m.SemiCommercialPage })),
);
const GuidesHubPage = lazy(() =>
  import('@/marketing').then((m) => ({ default: m.GuidesHubPage })),
);
const GuidePage = lazy(() =>
  import('@/marketing').then((m) => ({ default: m.GuidePage })),
);

function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-fog">
      <Spinner size="lg" colour="violet" />
    </div>
  );
}


function HomePlaceholder() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-fog">
      <div className="text-center">
        <img
          src="/lendaris/lendaris-mark.svg"
          alt="Lendaris"
          width={64}
          height={64}
          className="mx-auto mb-6"
        />
        <h1 className="text-h1 text-midnight">Lendaris</h1>
        <p className="text-body text-ink-500 mt-4">Get paid to take your loan. Coming soon.</p>
      </div>
    </div>
  );
}

function HashScroller() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (!hash) return;
    const id = hash.slice(1);
    const tryScroll = () => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };
    const t = setTimeout(tryScroll, 60);
    return () => clearTimeout(t);
  }, [pathname, hash]);
  return null;
}

export default function App() {
  useAuthBootstrap();

  return (
    <>
      <Toaster />
      <DevBypassBar />
      <HashScroller />
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Marketing website */}
          <Route element={<MarketingLayout />}>
            <Route path="/" element={<HomePage />} />
            <Route path="/how-it-works" element={<HowItWorksPage />} />
            <Route path="/your-share" element={<YourSharePage />} />
            <Route path="/finance/bridging" element={<BridgingFinancePage />} />
            <Route path="/finance/commercial" element={<CommercialMortgagesPage />} />
            <Route path="/finance/development" element={<DevelopmentFinancePage />} />
            <Route path="/finance/btl" element={<BuyToLetPage />} />
            <Route path="/buy-to-let-mortgages" element={<BuyToLetPage />} />
            <Route path="/semi-commercial-mortgages" element={<SemiCommercialPage />} />
            <Route path="/guides" element={<GuidesHubPage />} />
            <Route path="/guides/:slug" element={<GuidePage />} />
          </Route>

          {/* Auth */}
          <Route path="/sign-in" element={<SignInPage mode="sign-in" />} />
          <Route path="/sign-up" element={<SignInPage mode="sign-up" />} />
          <Route path="/magic-link-sent" element={<MagicLinkSentPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/auth/callback" element={<AuthCallbackPage />} />

          {/* Borrower portal (token-gated) */}
          <Route path="/portal/finance-search" element={<BorrowerPortalPage />} />

          {/* Eligibility */}
          <Route path="/eligibility" element={<EligibilityWizardPage />} />
          <Route path="/eligibility/rejected/:reason" element={<EligibilityRejectionPage />} />

          {/* Onboarding */}
          <Route path="/onboarding/:step" element={<OnboardingPage />} />

          {/* App — portal shell wraps all authenticated app routes */}
          <Route path="/app" element={<PortalLayout />}>
            <Route index element={<DashboardPage />} />
            <Route path="dashboard" element={<DashboardPage />} />
            <Route path="deals" element={<DealsListPage />} />
            <Route path="deals/new" element={<DealBuilderPage />} />
            <Route path="deals/:dealId" element={<DealBuilderPage />} />
            <Route path="deals/:dealId/claimed" element={<ClaimedDealLandingPage />} />
            <Route path="deals/:dealId/documents" element={<DocumentsPage />} />
            <Route path="deals/:dealId/compare" element={<LenderComparisonPage />} />
            <Route path="deals/:dealId/submit" element={<SubmissionConfirmPage />} />
            <Route path="deals/:dealId/submitted" element={<SubmissionSubmittedPage />} />
            <Route path="submissions/:submissionId" element={<CaseTrackerPage />} />
            <Route path="documents" element={<DocumentsViewPage />} />
            <Route path="earnings" element={<EarningsPage />} />
            <Route path="messages" element={<MessagesPage />} />
            <Route path="settings" element={<SettingsPage />} />
          </Route>

          {/* CRM — staff-only routes */}
          <Route path="/crm" element={<CRMLayout />}>
            <Route index element={<CRMDashboardPage />} />
            <Route path="dashboard" element={<CRMDashboardPage />} />
            <Route path="borrowers" element={<BorrowersListPage />} />
            <Route path="borrowers/:id" element={<BorrowerDetailPage />} />
            <Route path="deals" element={<CRMDealsListPage />} />
            <Route path="deals/:id" element={<CRMDealDetailPage />} />
            <Route path="document-review" element={<DocumentReviewQueuePage />} />
            <Route path="audit" element={<AuditLogPage />} />
            <Route path="submissions" element={<SubmissionsQueuePage />} />
            <Route path="submissions/:id" element={<SubmissionDetailPage />} />
            <Route path="lenders" element={<LendersListPage />} />
            <Route path="lenders/new" element={<LenderCreatePage />} />
            <Route path="lenders/:id" element={<LenderDetailPage />} />
            <Route path="lenders/:lenderId/products/new" element={<LenderProductEditPage />} />
            <Route path="lenders/:lenderId/products/:productId" element={<LenderProductEditPage />} />
            <Route path="lenders/:lenderId/form-templates/upload" element={<FormTemplateUploadPage />} />
            <Route path="form-templates/:id/map" element={<FormTemplateMapperPage />} />
            <Route path="commission/ledger" element={<CommissionLedgerPage />} />
            <Route path="commission/ledger/:id" element={<CommissionDetailPage />} />
            <Route path="commission/payouts" element={<PayoutBatchListPage />} />
            <Route path="commission/payouts/:id" element={<PayoutBatchDetailPage />} />
            <Route path="analytics/funnel" element={<FunnelDashboardPage />} />
            <Route path="analytics/lenders" element={<LenderAnalyticsDashboardPage />} />
            <Route path="analytics/ai-cost" element={<AICostDashboardPage />} />
            <Route path="analytics/revenue" element={<RevenueDashboardPage />} />
            <Route path="operations/bdms" element={<BDMListPage />} />
            <Route path="agencies" element={<AgenciesPage />} />
            <Route path="admin/staff-users" element={<StaffUsersPage />} />
            <Route path="admin/settings" element={<SystemSettingsPage />} />
            <Route path="admin/brickflow" element={<BrickflowSettingsPage />} />
            <Route path="messaging" element={<CRMMessagingPage />} />
            <Route path="messaging/notification-log" element={<NotificationLogPage />} />
            <Route path="messaging/templates" element={<TemplatesPage />} />
            <Route path="finance/lender-search" element={<LenderSearchPage />} />
            <Route path="finance/deal-pipeline" element={<DealPipelinePage />} />
            <Route path="finance/dip-tracker" element={<DipTrackerPage />} />
            <Route path="finance/action-queue" element={<BrickflowActionQueuePage />} />
            <Route path="finance/shortlists" element={<ShortlistReviewQueuePage />} />
            <Route path="finance/shortlists/:id" element={<ShortlistReviewDetailPage />} />
            <Route path="finance/analytics" element={<BrickflowAnalyticsPage />} />
          </Route>

          {/* Dev */}
          {import.meta.env.DEV && (
            <Route path="/_dev/components" element={<ComponentShowcase />} />
          )}

          <Route path="*" element={<HomePage />} />
        </Routes>
      </Suspense>
    </>
  );
}

function DevBypassBar() {
  if (!import.meta.env.DEV) return null;
  return (
    <div className="fixed bottom-4 right-4 z-[9999] flex gap-2 bg-midnight/90 backdrop-blur border border-white/10 rounded-2xl px-4 py-2.5 shadow-xl">
      <span className="text-xs font-bold text-white/40 uppercase tracking-wider self-center mr-1">Dev</span>
      <Link
        to="/app"
        className="text-xs font-semibold text-mint-400 hover:text-white transition-colors px-2 py-1 rounded-lg hover:bg-white/10"
      >
        Portal
      </Link>
      <Link
        to="/crm"
        className="text-xs font-semibold text-sky-400 hover:text-white transition-colors px-2 py-1 rounded-lg hover:bg-white/10"
      >
        CRM
      </Link>
      <Link
        to="/"
        className="text-xs font-semibold text-ink-400 hover:text-white transition-colors px-2 py-1 rounded-lg hover:bg-white/10"
      >
        Marketing
      </Link>
    </div>
  );
}
